import {
    actions,
    componentClasses,
    eventMap,
    sortState,
    views,
    defaultProps,
} from "../../../../core/components/table/head/cell";
import { views as tableViews } from "../../../../core/components/table/table";
import { types as sortType } from "../../../../core/js/data-grid/sort-by-column";
import {
    createStore,
    dispatchEvent,
    reducers,
    renderState,
} from "../../../../core/js/state-machine";
import { createCSSClass } from "../../../../core/js/utilities";
import ActionMenu from "../../action-menu/action-menu";
import { pinButtonContentTemplate } from "./action.template";

const TableHeadCell = (element, data) => {
    const dispose = () => {
        if (actionButton) {
            eventsToMonitorStates.forEach((eventName) => {
                actionButton.removeEventListener(eventName, handleStates);
            });
            actionMenuInstance.dispose();
        }
    };

    const resetSort = () => {
        if (sortStore) {
            sortStore.dispatch(actions.sort.set(sortType.unsorted));
        }
    };

    const handleSortBy = (sortBy) => () => {
        sortStore.dispatch(actions.sort.set(sortBy));
        dispatchEvent(eventMap.sort, element, sortStore.getState());
        actionButton.focus();
        actionMenuInstance.close();
    };

    /**
     * Handle the sortStore event when something is dispatched
     * @param {String} newState
     */
    const handleSortChange = (newState) => {
        api.sortBy = newState;

        renderState(views.sortBy.wrapper[newState], element);

        [].forEach.call(
            element.querySelectorAll('[data-dds="action-menu"] [data-value]'),
            (button) => {
                if (Object.keys(defaultProps.sort.actions).includes(button.dataset.value)) {
                    const state =
                        button.dataset.value === newState
                            ? views.sortBy.actionMenu[newState]
                            : views.sortBy.actionMenu.unchecked;

                    renderState(state, button);
                }
            }
        );

        const buttonLabel = sortButtonMergedLabels[newState];
        sortLabel.textContent = buttonLabel;
    };

    /**
     * Handle the pin/unpin action
     */
    const handlePin = () => {
        const isPinned = api.isLastPinnedColumn;
        pinStore.dispatch(isPinned ? actions.unpin() : actions.pin());
        dispatchEvent(isPinned ? eventMap.unpin : eventMap.pin, element, pinStore.getState());
        actionButton.focus();
        actionMenuInstance.close();
    };

    /**
     * Handle the action menu state
     * @param {Boolean} isPinned Is this cell pinned
     */
    const handlePinChange = (isPinned) => {
        api.isPinned = isPinned;
        api.isLastPinnedColumn = isPinned && isLastPinnedColumn;
        switchActionMenuPinButton(api.isLastPinnedColumn);
    };

    /**
     * Apply the pin properties to this cell
     * @param {Boolean} isPinnedColumn Is this column the pinned one
     * @param {Number} leftPosition Class name with the calculated values
     */
    const pin = (isPinnedColumn, leftPosition) => {
        const classList = Array.from(
            isPinnedColumn ? tableViews.pin.lastColumn.classList : tableViews.pin.pinned.classList
        );
        isLastPinnedColumn = isPinnedColumn;
        if (leftPosition) {
            setLeftPosition(leftPosition);
            updateColumnStyling();
        }
        dispatchEvent(eventMap.styleUpdated, element, { classList, columnClassName });
        if (pinStore) {
            pinStore.dispatch(actions.pin());
        }
    };

    /**
     * Remove the pin properties from this cell
     */
    const unpin = () => {
        const classList = Array.from(tableViews.pin.unpinned.classList);
        isLastPinnedColumn = false;
        dispatchEvent(eventMap.styleUpdated, element, { classList, columnClassName });
        if (pinStore) {
            pinStore.dispatch(actions.unpin());
        }
    };

    /**
     * Reset the left position of the column
     */
    const resetPosition = () => {
        setLeftPosition(null);
        updateColumnStyling();
    };

    /**
     * Set the left position of the column
     * @param {Number|null} value Left position
     */
    const setLeftPosition = (value) => {
        leftPosition = value;
    };

    /**
     * Switch the content of the pin button on the action menu
     * @param {Boolean} isPinned If this cell is pinned or not
     */
    const switchActionMenuPinButton = (isPinned) => {
        if (!pinButton) {
            return;
        }
        pinButton.innerHTML = pinButtonContentTemplate(isPinned, pinSettings);
    };

    const handleStates = (event) => {
        let state = views.rest;
        if (event.type === "focus") {
            state = views.states.focus;
            if (document.body.classList.contains("user-is-tabbing")) {
                dispatchEvent(eventMap.headCellFocusChanged, element, { type: "tab" });
            } else {
                dispatchEvent(eventMap.headCellFocusChanged, element, { type: "click" });
            }
        }
        renderState(state, element);
    };

    // Resize handle commented until the a11y review
    // const moveResizeHandler = (event) => {
    //     const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    //     const newWidth = startWidth + clientX - startX;
    //     if (newWidth <= initialMinWidth) {
    //         return;
    //     }
    //     width = newWidth;
    //     updateColumnStyling();
    //     if (api.isPinned) {
    //         dispatchEvent(eventMap.resized, element);
    //     }
    // };

    // const resizeHandlerDrop = () => {
    //     document.documentElement.removeEventListener("mousemove", moveResizeHandler, false);
    //     document.documentElement.removeEventListener("touchmove", moveResizeHandler, false);
    //     document.documentElement.removeEventListener("mouseup", resizeHandlerDrop, false);
    //     document.documentElement.removeEventListener("touchend", resizeHandlerDrop, false);
    // };

    // const resizeHandlerDrag = (event) => {
    //     startX = event.touches ? event.touches[0].clientX : event.clientX;
    //     startWidth = parseInt(document.defaultView.getComputedStyle(element).width, 10);
    //     document.documentElement.addEventListener("mousemove", moveResizeHandler, false);
    //     document.documentElement.addEventListener("touchmove", moveResizeHandler, false);
    //     document.documentElement.addEventListener("mouseup", resizeHandlerDrop, false);
    //     document.documentElement.addEventListener("touchend", resizeHandlerDrop, false);
    // };

    // const resizeHandle = element.querySelector(`.${componentClasses.resizeHandle}`);
    // if (resizeHandle && data.resizable) {
    //     resizeHandle.addEventListener("mousedown", resizeHandlerDrag, false);
    //     resizeHandle.addEventListener("touchstart", resizeHandlerDrag, false);
    // }

    /**
     * Set the column width
     * @param {Number|null} newWidth Width value
     */
    const setWidth = (newWidth) => {
        width = newWidth;
    };

    /**
     * Updates the column styling properties
     */
    const updateColumnStyling = () => {
        const style = {};

        if (width) {
            style.width = `${width}px`;
        } else if (initialWidth) {
            style.width = `${initialWidth}px`;
        }

        if (fit || width) {
            style.flex = `0 0 auto`;
        }

        if (initialMinWidth) {
            style["min-width"] = `${initialMinWidth}px`;
        }

        if (initialMaxWidth) {
            style["max-width"] = `${initialMaxWidth}px`;
        }

        if (leftPosition) {
            style.left = `${leftPosition}px`;
        }

        createCSSClass(`.dds__th.${columnClassName}, .dds__td.${columnClassName}`, style);
    };

    const eventsToMonitorStates = [
        "mousedown",
        "touchstart",
        "touchend",
        "touchcancel",
        "mouseup",
        "focus",
        "blur",
    ];

    // Stores
    let sortStore;
    let pinStore;

    // Action Buttons
    const actionButton = element.querySelector(`.${componentClasses.actionButton.rest}`);
    const pinButton = element.querySelector("[data-value=pin]");
    let actionMenuInstance;

    // Column sizing
    const initialWidth = data.width ? data.width : null;
    const initialMinWidth = data.minWidth ? data.minWidth : initialWidth;
    const initialMaxWidth = data.maxWidth ? data.maxWidth : null;
    const fit = data.fit || initialWidth ? true : false;

    // Options
    const columnClassName = data.colClass;
    const isPinnable = data.pinnable ? true : false;
    const sortBy = data.sortBy in sortType ? data.sortBy : undefined;
    let isLastPinnedColumn = false;
    let leftPosition;
    // Resize handle commented until the a11y review
    // let startX;
    // let startWidth;
    let width;

    // Labels
    const sortLabel = element.querySelector(`.${componentClasses.buttonScreenReader}`);
    const sortButtonMergedLabels = Object.assign(defaultProps.sort.labels, data.sortLabels || {});
    const pinSettings = Object.assign(defaultProps.pin, data.pin || {});

    if (actionButton) {
        sortStore = createStore(reducers.iterator(sortState), sortBy);
        sortStore.subscribe(handleSortChange);

        pinStore = createStore(reducers.boolReducer, false);
        pinStore.subscribe(handlePinChange);

        actionMenuInstance = new ActionMenu(element.querySelector('[data-dds="action-menu"]'), {
            alignment: "end",
        });

        eventsToMonitorStates.forEach((eventName) => {
            actionButton.addEventListener(eventName, handleStates);
        });

        if (sortBy) {
            [].forEach.call(element.querySelectorAll("[data-value]"), (button) => {
                if (Object.keys(defaultProps.sort.actions).includes(button.dataset.value)) {
                    button.addEventListener("click", handleSortBy(button.dataset.value));
                }
            });
        }

        if (isPinnable && pinButton) {
            pinButton.addEventListener("click", handlePin);
        }
    }

    if (columnClassName) {
        renderState({ classList: [columnClassName] }, element);
    }

    // This should run for the first time to start the initial
    // column calculation
    updateColumnStyling();

    const api = {
        setLeftPosition,
        resetPosition,
        pin,
        unpin,

        fit,
        updateColumnStyling,
        setWidth,

        sortBy,
        data,
        element,
        dispose,
        resetSort,
    };

    return api;
};
export default TableHeadCell;
